import React from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from 'components/general/pageHeader';
import queryString from 'query-string';

import { Forms, Loading, Paging, SystemText } from 'e4shared';

import Person from './components/person';
import Post from './components/post';
import Question from './components/question';
import Task from './components/task';
import LMS from './components/lms';
import Richardson from './components/richardson';
import NoResults from 'components/general/noResults';

import { search as runSearch, loadCompetency } from 'actions/search';

import './search.css';
import { apiGet } from 'services';

export const Search = ({ location, match }) => {
    const dispatch = useDispatch();

    const values = queryString.parse(location.search);
    const initSearch = match.params.search;
    const initTag = match.params.tag;
    const initComp = values.competency ? values.competency : '';
    const initCompetencyName = values.competency_name ? values.competency_name : '';
    const initProfile = values.user ? values.user : '';
    const initManager = values.manager ? values.manager : '';

    const [page, setPage] = React.useState(1);
    const [competencyId, setCompetencyId] = React.useState(null);
    const [competencyName, setCompetencyName] = React.useState(null);
    const [profileId, setProfileId] = React.useState(null);
    const [managerId, setManagerId] = React.useState(null);
    const [types, setTypes] = React.useState([]);
    const [search, setSearch] = React.useState('');

    const { coaching, downline, downlineCompetencies, user } = useSelector(state => state.user);
    const { competency, loading, results } = useSelector(state => state.search);

    React.useEffect(() => {
        initTag && setSearch(`#${initTag}`);
        initSearch && setSearch(initSearch);
        initComp && setCompetencyId(initComp);
        initCompetencyName && setCompetencyName(initCompetencyName);
        initProfile && setProfileId(initProfile);
        initManager && setManagerId(initManager);
    }, [initTag, initSearch, initComp, initManager]);

    React.useEffect(() => {
        setPage(1);
    }, [search, types, competencyId, competencyName, profileId, managerId]);

    React.useEffect(() => {
        const cleanSearch = search.replace('#', '');
        if (cleanSearch.length > 2 || competencyId || competencyName || managerId) {
            dispatch(runSearch(cleanSearch, competencyId, competencyName, profileId, managerId, types, page));
        }
    }, [search, types, competencyId, competencyName, profileId, managerId, page]);

    React.useEffect(() => {
        competencyId && dispatch(loadCompetency(competencyId));
    }, [competencyId]);

    const competenciesMapped = downlineCompetencies
        ? downlineCompetencies.map(c => ({ id: c.id, name: `${c.area} - ${c.name}` }))
        : [];

    const resultsMessage = competency?.id
        ? SystemText('Search/Results/Message', `${competency?.area} - ${competency?.name}`)
        : '';

    const managerResultsMessage = SystemText('Search/Results/Message/Manager', values.name);

    const toggleType = type => {
        types.find(t => t === type) ? setTypes(types.filter(t => t !== type)) : setTypes(types.concat([type]));
    };

    const hasPeople = results && results.people > 0;
    const hasQuestions = results && results.questions > 0;
    const hasPosts = results && results.posts > 0;
    const hasTasks = results && results.tasks > 0;
    const hasLMSCourses = results && results.lmsCourses > 0;
    const hasRichardsonContent = results && results.richardsonContent > 0;

    const noResults = loading ? (
        <Loading />
    ) : search.length >= 3 ? (
        <div className="col-md-12">
            <NoResults title={SystemText('Search/NoResults/Title')} message={SystemText('Search/NoResults/Message')} />
        </div>
    ) : competencyId || competencyName ? (
        <div className="col-md-12">
            <NoResults
                title={SystemText('Search/NoResults/Title')}
                message={SystemText('Search/NoResults/Message/Competency')}
            />
        </div>
    ) : managerId ? (
        <div className="col-md-12">
            <NoResults
                title={SystemText('Search/NoResults/Title')}
                message={SystemText('Search/NoResults/Message/Manager')}
            />
        </div>
    ) : (
        <div className="col-md-12">
            <p>{SystemText('Search/3Letters')}</p>
        </div>
    );

    return (
        <div>
            <PageHeader title={SystemText('Search/Title')} />
            <section className="content mtm10" id="search-results" data-testid="search-content">
                <div className="row">
                    <div className="col-md-12">
                        {competencyId || competencyName ? (
                            <h2 className="competency-header">{resultsMessage}</h2>
                        ) : managerId ? (
                            <h2 className="competency-header">{managerResultsMessage}</h2>
                        ) : (
                            <Forms.Text
                                id="search"
                                label={SystemText('Search/Fields/Search/Label')}
                                value={search}
                                onChange={value => setSearch(value?.replace('#', '').replace('@', ''))}
                                className="input-lg"
                                placeholder={SystemText('Search/Fields/Search/Placeholder')}
                                icon="fa-search"
                            />
                        )}
                    </div>
                    <div className="col-md-6 hidden">
                        <Forms.Select
                            id="competency_Id"
                            label={SystemText('Search/Fields/Competency/Label')}
                            data={competenciesMapped}
                            value={competencyId}
                            onChange={setCompetencyId}
                        />
                    </div>
                </div>
                {hasPeople + hasQuestions + hasPosts + hasTasks + hasLMSCourses + hasRichardsonContent > 1 && (
                    <div className="row mb10">
                        <div className="col-md-12 mb10">
                            <label>{SystemText('Search/Buttons/Label')}</label>
                            <div className="table-btns">
                                <button
                                    data-testid="all-button"
                                    onClick={() => setTypes([])}
                                    className={`btn ${types.length === 0 ? 'btn-primary' : 'btn-default'}`}
                                >
                                    {types.length === 0 && <i className={`fa fa-check mr5`} />}
                                    {SystemText('Search/Buttons/Everything')}
                                </button>
                                {hasPeople && (
                                    <button
                                        data-testid="people-button"
                                        onClick={() => toggleType('USER')}
                                        className={`btn ${types.some(t => t === 'USER') ? 'btn-primary' : 'btn-default'}`}
                                    >
                                        {types.some(t => t === 'USER') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/People')}
                                    </button>
                                )}
                                {hasQuestions && (
                                    <button
                                        data-testid="question-button"
                                        onClick={() => toggleType('QUESTION')}
                                        className={`btn ${
                                            types.some(t => t === 'QUESTION') ? 'btn-primary' : 'btn-default'
                                        }`}
                                    >
                                        {types.some(t => t === 'QUESTION') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/Knowledge')}
                                    </button>
                                )}
                                {hasPosts && (
                                    <button
                                        data-testid="post-button"
                                        onClick={() => toggleType('POST')}
                                        className={`btn ${types.some(t => t === 'POST') ? 'btn-primary' : 'btn-default'}`}
                                    >
                                        {types.some(t => t === 'POST') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/Posts')}
                                    </button>
                                )}
                                {hasTasks && (
                                    <button
                                        data-testid="objective-button"
                                        onClick={() => toggleType('TASK')}
                                        className={`btn ${types.some(t => t === 'TASK') ? 'btn-primary' : 'btn-default'}`}
                                    >
                                        {types.some(t => t === 'TASK') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/Objectives')}
                                    </button>
                                )}
                                {hasLMSCourses && (
                                    <button
                                        data-testid="lms-button"
                                        onClick={() => toggleType('LMS')}
                                        className={`btn ${types.some(t => t === 'LMS') ? 'btn-primary' : 'btn-default'}`}
                                    >
                                        {types.some(t => t === 'LMS') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/LMS')}
                                    </button>
                                )}
                                {hasRichardsonContent && (
                                    <button
                                        data-testid="richardson-button"
                                        onClick={() => toggleType('RICHARDSON')}
                                        className={`btn ${
                                            types.some(t => t === 'RICHARDSON') ? 'btn-primary' : 'btn-default'
                                        }`}
                                    >
                                        {types.some(t => t === 'RICHARDSON') && <i className={`fa fa-check mr5`} />}
                                        {SystemText('Search/Buttons/Richardson')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="row">
                    {!loading && results.results && results.results.length > 0
                        ? results.results.map((r, i) => (
                              <div key={`${r.type}_${r.id}_${i}`} className="col-md-12">
                                  {r.type === 'profile' && (
                                      <Person data={r} user={user} coaching={coaching} downline={downline} />
                                  )}
                                  {r.type === 'post' && <Post data={r} />}
                                  {r.type === 'question' && <Question data={r} />}
                                  {r.type === 'task' && <Task data={r} userId={user.id} />}
                                  {r.type === 'lms' && <LMS data={r} userId={user.id} />}
                                  {r.type === 'richardson' && <Richardson data={r} userId={user.id} />}
                              </div>
                          ))
                        : noResults}
                </div>
                <div className="text-center mt10">
                    <Paging className="btn-primary" goToPage={setPage} recordCount={results.records} page={page} />
                </div>
            </section>
        </div>
    );
};

export default withRouter(Search);
